import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DataManagement = ({ onDataChange }) => {
  const [importFile, setImportFile] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const handleExportData = () => {
    try {
      const companies = localStorage.getItem('companies') || '[]';
      const users = localStorage.getItem('users') || '[]';
      
      const exportData = {
        companies: JSON.parse(companies),
        users: JSON.parse(users),
        exportDate: new Date()?.toISOString(),
        version: '1.0'
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `downtimeiq-backup-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      URL.revokeObjectURL(url);

      alert('Data exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export data. Please try again.');
    }
  };

  const handleImportData = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e?.target?.result);
        
        if (importedData?.companies) {
          localStorage.setItem('companies', JSON.stringify(importedData?.companies));
        }
        if (importedData?.users) {
          localStorage.setItem('users', JSON.stringify(importedData?.users));
        }

        alert('Data imported successfully!');
        if (onDataChange) onDataChange();
        setImportFile(null);
      } catch (error) {
        console.error('Import error:', error);
        alert('Failed to import data. Please check the file format.');
      }
    };
    reader?.readAsText(file);
  };

  const handlePurgeData = (dataType) => {
    setConfirmAction(dataType);
    setShowConfirmDialog(true);
  };

  const confirmPurge = () => {
    try {
      if (confirmAction === 'companies') {
        localStorage.removeItem('companies');
        alert('Company data purged successfully!');
      } else if (confirmAction === 'users') {
        localStorage.removeItem('users');
        alert('User data purged successfully!');
      } else if (confirmAction === 'all') {
        localStorage.removeItem('companies');
        localStorage.removeItem('users');
        alert('All data purged successfully!');
      }
      
      if (onDataChange) onDataChange();
      setShowConfirmDialog(false);
      setConfirmAction(null);
    } catch (error) {
      console.error('Purge error:', error);
      alert('Failed to purge data. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-2">Data Management</h2>
        <p className="text-sm text-muted-foreground">Export, import, and manage your application data</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Download" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Export Data</h3>
              <p className="text-xs text-muted-foreground">Download backup of all data</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Export all companies, users, and plant data to a JSON file for backup or migration purposes.
          </p>
          <Button variant="default" iconName="Download" onClick={handleExportData} fullWidth>
            Export All Data
          </Button>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <Icon name="Upload" size={20} className="text-accent" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Import Data</h3>
              <p className="text-xs text-muted-foreground">Restore from backup file</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Import data from a previously exported JSON file. This will replace existing data.
          </p>
          <label htmlFor="import-file">
            <Button variant="outline" iconName="Upload" fullWidth asChild>
              <span>Select File to Import</span>
            </Button>
            <input
              id="import-file"
              type="file"
              accept=".json"
              onChange={handleImportData}
              className="hidden"
            />
          </label>
        </div>
      </div>

      <div className="bg-error/5 rounded-lg border border-error/20 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-error/10 flex items-center justify-center">
            <Icon name="AlertTriangle" size={20} className="text-error" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Danger Zone</h3>
            <p className="text-xs text-muted-foreground">Irreversible data operations</p>
          </div>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 bg-card rounded-lg border border-border">
            <div>
              <p className="font-medium text-sm text-foreground">Purge Company Data</p>
              <p className="text-xs text-muted-foreground">Remove all company and plant records</p>
            </div>
            <Button variant="outline" size="sm" iconName="Trash2" onClick={() => handlePurgeData('companies')}>
              Purge
            </Button>
          </div>

          <div className="flex items-center justify-between p-4 bg-card rounded-lg border border-border">
            <div>
              <p className="font-medium text-sm text-foreground">Purge User Data</p>
              <p className="text-xs text-muted-foreground">Remove all user accounts</p>
            </div>
            <Button variant="outline" size="sm" iconName="Trash2" onClick={() => handlePurgeData('users')}>
              Purge
            </Button>
          </div>

          <div className="flex items-center justify-between p-4 bg-error/10 rounded-lg border border-error/30">
            <div>
              <p className="font-medium text-sm text-error">Reset All Data</p>
              <p className="text-xs text-muted-foreground">Remove all application data</p>
            </div>
            <Button variant="danger" size="sm" iconName="Trash2" onClick={() => handlePurgeData('all')}>
              Reset All
            </Button>
          </div>
        </div>
      </div>

      {showConfirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card rounded-xl border border-border p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-lg bg-error/10 flex items-center justify-center">
                <Icon name="AlertTriangle" size={24} className="text-error" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-foreground">Confirm Data Purge</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone</p>
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground mb-6">
              Are you sure you want to purge {confirmAction === 'all' ? 'all data' : `${confirmAction} data`}? 
              This will permanently delete the selected data from localStorage.
            </p>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowConfirmDialog(false);
                  setConfirmAction(null);
                }}
                fullWidth
              >
                Cancel
              </Button>
              <Button variant="danger" onClick={confirmPurge} fullWidth>
                Confirm Purge
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataManagement;